import React, { useState, useEffect } from 'react';
import MemberForm from './MemberForm';
import MemberList from './MemberList';
import VehicleForm from './VehicleForm';
import VehicleList from './VehicleList';
import SettingsForm from './SettingsForm';
import SiteContentForm from './SiteContentForm';
import ConfirmationModal from './ConfirmationModal';
import CloudinaryConfigForm from './CloudinaryConfigForm';
import { collection, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebaseConfig';
import { Home, Car, Users, FileText, LayoutGrid, Settings, List, CheckCircle, Wrench, Shield, Palette, Info } from 'lucide-react';
import NavMenuForm from './NavMenuForm';

const AdminDashboard = ({ user }) => {
    const [vehicles, setVehicles] = useState([]);
    const [members, setMembers] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchVehicles = async () => {
        const querySnapshot = await getDocs(collection(db, 'vehicles'));
        const vehicleList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setVehicles(vehicleList);
    };

    const fetchMembers = async () => {
        const querySnapshot = await getDocs(collection(db, 'members'));
        const memberList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMembers(memberList);
    };

    const [activeSection, setActiveSection] = useState('Dashboard');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Jednoduchý, konzistentní wrapper pro obsah sekcí
    const Section = ({ title, actions, children }) => (
        <section className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-5 py-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
                {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
            </div>
            <div className="p-5">{children}</div>
        </section>
    );

    const renderContent = () => {
        switch (activeSection) {
            case 'Dashboard':
                return (
                    <Section title="Dashboard">
                        <p className="text-gray-600">Vítejte ve vašem administračním prostředí.</p>
                        <div className="mt-6">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2">Poslední změny</h3>
                            <div className="space-y-3">
                                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                                        <div>
                                            <div className="font-medium text-gray-900">Instalace modulu pro editaci kontentu</div>
                                            <div className="text-sm text-gray-500">VIZE CRM Content Editor</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                    <div className="flex items-start gap-3">
                                        <Palette className="w-5 h-5 text-indigo-600 mt-0.5" />
                                        <div>
                                            <div className="font-medium text-gray-900">Úprava tématu administrace</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                    <div className="flex items-start gap-3">
                                        <Shield className="w-5 h-5 text-red-600 mt-0.5" />
                                        <div>
                                            <div className="font-medium text-gray-900">Úprava zabezpečení administrace</div>
                                            <div className="text-sm text-gray-500">VIZE CRM VIC3 SECURITY</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                    <div className="flex items-start gap-3">
                                        <Wrench className="w-5 h-5 text-amber-600 mt-0.5" />
                                        <div>
                                            <div className="font-medium text-gray-900">Instalace modulu NavBarEditor</div>
                                            <div className="text-sm text-gray-500">VIZE 1.5 OLD VERSION</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6">
                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                                    <div>
                                        <div className="font-medium text-blue-900">Technický administrátor</div>
                                        <div className="text-sm text-blue-800">
                                            Pro úpravy webu můžete nadále kontaktovat technického administrátora. Další úpravy budou účtovány. Opravy chyb způsobených technickým administrátorem jsou zdarma.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Section>
                );
            case 'Vehicles':
                return (
                    <Section
                        title="Vozidla"
                        actions={
                            <button
                                className="inline-flex items-center gap-2 bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                                onClick={() => {
                                    setEditingItem(null);
                                    setIsModalOpen(true);
                                }}
                            >
                                Přidat nové vozidlo
                            </button>
                        }
                    >
                        <VehicleList
                            vehicles={vehicles}
                            onEdit={handleEditItem}
                            onDelete={(id) => {
                                setItemToDelete({ id, type: 'vehicles' });
                                setIsConfirmModalOpen(true);
                            }}
                        />
                    </Section>
                );
            case 'Members':
                return (
                    <Section
                        title="Členové"
                        actions={
                            <button
                                className="inline-flex items-center gap-2 bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                                onClick={() => {
                                    setEditingItem(null);
                                    setIsModalOpen(true);
                                }}
                            >
                                Přidat nového člena
                            </button>
                        }
                    >
                        <MemberList
                            members={members}
                            onEdit={handleEditItem}
                            onDelete={(id) => {
                                setItemToDelete({ id, type: 'members' });
                                setIsConfirmModalOpen(true);
                            }}
                        />
                    </Section>
                );
            case 'Texts':
                return (
                    <Section title="Texty na stránce">
                        <SettingsForm />
                    </Section>
                );
            case 'SiteContent':
                return (
                    <Section title="Kontent stránky">
                        <SiteContentForm />
                    </Section>
                );
            case 'Navigation':
                return (
                    <Section title="Navigační menu">
                        <NavMenuForm />
                    </Section>
                );
            case 'Configuration':
                return (
                    <Section title="Konfigurace">
                        <CloudinaryConfigForm />
                    </Section>
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        setLoadingData(true);
        fetchVehicles();
        fetchMembers();
        setLoadingData(false);

    }, []);


    const handleEditItem = (item) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleDeleteItem = async () => {
        if (!itemToDelete) return;
        const { id, type } = itemToDelete;

        try {
            await deleteDoc(doc(db, type, id));
            if (type === 'vehicles') {
                fetchVehicles();
            } else if (type === 'members') {
                fetchMembers();
            }
        } catch (error) {
            console.error(`Error deleting ${type}:`, error);
        } finally {
            setIsConfirmModalOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);

        if (activeSection === 'Vehicles') {
            fetchVehicles();
        } else if (activeSection === 'Members') {
            fetchMembers();
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (e) {
            console.error('Chyba při odhlášení:', e);
        }
    };

    return (
        <div className="flex w-full min-h-screen bg-gray-50">

            <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b">
                    <div className="text-lg font-semibold text-indigo-700">Admin Panel</div>
                    <div className="text-xs text-gray-500">Správa obsahu</div>
                </div>
                <nav className="flex-1 p-2">
                    <ul className="space-y-1">
                        <li>
                            <button className={`w-full inline-flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${activeSection === 'Dashboard' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'}`} onClick={() => setActiveSection('Dashboard')}>
                                <Home className="w-4 h-4" />
                                <span>Dashboard</span>
                            </button>
                        </li>
                        <li>
                            <button className={`w-full inline-flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${activeSection === 'Vehicles' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'}`} onClick={() => setActiveSection('Vehicles')}>
                                <Car className="w-4 h-4" />
                                <span>Vozidla</span>
                            </button>
                        </li>
                        <li>
                            <button className={`w-full inline-flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${activeSection === 'Members' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'}`} onClick={() => setActiveSection('Members')}>
                                <Users className="w-4 h-4" />
                                <span>Členové</span>
                            </button>
                        </li>
                        <li>
                            <button className={`w-full inline-flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${activeSection === 'Texts' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'}`} onClick={() => setActiveSection('Texts')}>
                                <FileText className="w-4 h-4" />
                                <span>Texty na stránce</span>
                            </button>
                        </li>
                        <li>
                            <button className={`w-full inline-flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${activeSection === 'SiteContent' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'}`} onClick={() => setActiveSection('SiteContent')}>
                                <LayoutGrid className="w-4 h-4" />
                                <span>Kontent stránky</span>
                            </button>
                        </li>
                        <li>
                            <button className={`w-full inline-flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${activeSection === 'Navigation' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'}`} onClick={() => setActiveSection('Navigation')}>
                                <List className="w-4 h-4" />
                                <span>Navigační menu</span>
                            </button>
                        </li>
                        <li>
                            <button className={`w-full inline-flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${activeSection === 'Configuration' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'}`} onClick={() => setActiveSection('Configuration')}>
                                <Settings className="w-4 h-4" />
                                <span>Konfigurace</span>
                            </button>
                        </li>
                    </ul>
                </nav>
            <div className="p-4 border-t mt-auto">
                <div className="text-sm text-gray-600 mb-2">
                    Přihlášeno: <span className="font-medium">{user?.email || '—'}</span>
                </div>
                <button
                    onClick={handleLogout}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                    Odhlásit se
                </button>
            </div>
            </aside>

            <main className="flex-1 p-6 text-gray-800">
                <div className="max-w-6xl mx-auto">
                    {loadingData ? (
                        <p>Loading data...</p>
                    ) : (
                        renderContent()
                    )}
                </div>
            </main>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold">{editingItem ? `Edit ${activeSection.slice(0, -1)}` : `Add New ${activeSection.slice(0, -1)}`}</h3>
                            <button onClick={handleCloseModal} className="text-gray-600 hover:text-gray-900">
                                &times;
                            </button>
                        </div>

                        {activeSection === 'Vehicles' && <VehicleForm itemData={editingItem} onClose={handleCloseModal} />}
                        {activeSection === 'Members' && <MemberForm itemData={editingItem} onClose={handleCloseModal} />}
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleDeleteItem}
                title="Potvrzení odstranění"
                message={`Opravdu chcete odstranit tuto položku? Změna je nenávratná.`}
            />
        </div>
    );
};

export default AdminDashboard;